import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { SidebarProvider } from 'equal-ds-ui';
import './App.css';
import Header from './components/Header';
import { FinProSidebar } from './components/sidebar/FinProSidebar';
import { sidebarBridge } from './services/sidebar-bridge';
import { envService } from './services/env.service';
import {
  transformMenuListToGroups,
  loadUserInfoFromSession,
  mapRouteToItemId,
} from './components/sidebar/sidebar-transforms';
import type { TransformedSidebarData } from './components/sidebar/sidebar-types';

// v2-master: Layout constants and types
const SIDEBAR_FULL_WIDTH = 255;
const SIDEBAR_COLLAPSED_WIDTH = 70;

type ViewportSize = 'mobile' | 'tablet' | 'desktop';

export interface SidebarLayoutState {
  mode: 'expanded' | 'collapsed' | 'overlay';
  width: number;
}

interface SidebarAppProps {
  activeItemId?: string | null;
  onLayoutChange?: (state: SidebarLayoutState) => void;
  headerContainer?: HTMLElement | null;
}

const getViewport = (): ViewportSize => {
  if (typeof window === 'undefined') {
    return 'desktop';
  }

  const width = window.innerWidth;
  if (width <= 767) {
    return 'mobile';
  }
  if (width <= 1023) {
    return 'tablet';
  }
  return 'desktop';
};

const getDefaultOpenState = (viewport: ViewportSize) => viewport === 'desktop';

const getLayoutState = (viewport: ViewportSize, open: boolean): SidebarLayoutState => {
  if (viewport === 'mobile') {
    return {
      mode: 'overlay',
      width: open ? SIDEBAR_FULL_WIDTH : 0,
    };
  }

  if (viewport === 'tablet') {
    if (open) {
      return { mode: 'expanded', width: SIDEBAR_FULL_WIDTH };
    }
    return { mode: 'collapsed', width: SIDEBAR_COLLAPSED_WIDTH };
  }

  return open
    ? { mode: 'expanded', width: SIDEBAR_FULL_WIDTH }
    : { mode: 'collapsed', width: SIDEBAR_COLLAPSED_WIDTH };
};

const SidebarApp: React.FC<SidebarAppProps> = ({ activeItemId: externalActiveItemId, onLayoutChange, headerContainer }) => {
  const initialViewport = useMemo(() => getViewport(), []);
  const [viewport, setViewport] = useState<ViewportSize>(initialViewport);
  const [isOpen, setIsOpen] = useState<boolean>(getDefaultOpenState(initialViewport));
  const [headerHost, setHeaderHost] = useState<HTMLElement | null>(headerContainer ?? null);

  // Load and transform sidebar data from sessionStorage
  const [sidebarData, setSidebarData] = useState<TransformedSidebarData | null>(null);
  const [activeItemId, setActiveItemId] = useState<string | null>(externalActiveItemId || null);

  // Load menu data from sessionStorage on mount
  useEffect(() => {
    const data = loadUserInfoFromSession();
    if (data && data.menuList.length > 0) {
      const transformed = transformMenuListToGroups(data.menuList);
      setSidebarData(transformed);
      console.log('[App] Sidebar data loaded:', transformed);
    } else {
      console.warn('[App] No menu data available, using default sidebar');
      // Will use FinProSidebar's default SIDEBAR_ITEMS
    }
  }, []);

  // Update activeItemId when external prop changes
  useEffect(() => {
    if (externalActiveItemId !== undefined) {
      setActiveItemId(externalActiveItemId);
    }
  }, [externalActiveItemId]);

  // Listen for Angular route changes and update active item
  useEffect(() => {
    const handleRouteChange = (event: Event) => {
      const customEvent = event as CustomEvent<{ currentRoute: string }>;
      if (!sidebarData) return;

      const { currentRoute } = customEvent.detail;
      console.log('[App] Angular route changed:', currentRoute);

      // Map route to itemId
      const itemId = mapRouteToItemId(currentRoute, sidebarData.routeToItemMap);
      if (itemId) {
        console.log('[App] Mapped to itemId:', itemId);
        setActiveItemId(itemId);
      }
    };

    window.addEventListener('angular-route-change', handleRouteChange);

    return () => {
      window.removeEventListener('angular-route-change', handleRouteChange);
    };
  }, [sidebarData]);

  // Handle viewport changes
  useEffect(() => {
    const handleResize = () => {
      setViewport(getViewport());
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    setIsOpen(getDefaultOpenState(viewport));
  }, [viewport]);

  const layoutState = useMemo(() => getLayoutState(viewport, isOpen), [viewport, isOpen]);

  useEffect(() => {
    if (headerHost) {
      return;
    }

    const element = document.getElementById('react-header-container');
    if (element instanceof HTMLElement) {
      setHeaderHost(element);
    }
  }, [headerHost]);

  useEffect(() => {
    const event = new CustomEvent<SidebarLayoutState>('react-sidebar-state', {
      detail: layoutState,
    });
    window.dispatchEvent(event);
    onLayoutChange?.(layoutState);
  }, [layoutState, onLayoutChange]);

  // Handle navigation from sidebar - trigger Angular navigation via bridge
  const handleNavigation = useCallback(
    (itemId: string) => {
      console.log('[App] Navigation requested for itemId:', itemId);

      // Special handling for Integration Docs - opens in new tab like Angular sidebar
      if (itemId === 'integration-docs') {
        const docsUrl = envService.downloadUrl;
        window.open(docsUrl, '_blank');
        return; // Don't update active state or navigate internally
      }

      // Update active item state
      setActiveItemId(itemId);

      // Trigger Angular navigation via bridge service
      sidebarBridge.handleMenuItemClick(itemId);
    },
    []
  );

  return (
    <div className="equal-ds-root">
      <SidebarProvider
        open={isOpen}
        onOpenChange={(next: boolean) => setIsOpen(next)}
        defaultOpen={getDefaultOpenState(viewport)}
        activeItem={activeItemId}
        onActiveItemChange={(itemId: string | null) => {
          if (itemId) {
            handleNavigation(itemId);
          }
        }}
      >
        <FinProSidebar
          headerText="FinPro Admin"
          menuGroups={sidebarData?.groups}
          activeItemId={activeItemId}
          onItemClick={handleNavigation}
        />
        {headerHost ? createPortal(<Header />, headerHost) : null}
      </SidebarProvider>
    </div>
  );
};

export default SidebarApp;
